import axios from "axios";

const XANO_URL = process.env.REACT_APP_XANO_API_URL;

const TOKEN_KEY = "brontoy_auth_token";
const REFRESH_KEY = "brontoy_refresh_token";
const VERIF_SESSION_KEY = "brontoy_verif_session";
const PENDING_EMAIL_KEY = "brontoy_pending_email";

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => t && localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  setRefresh: (t) => t && localStorage.setItem(REFRESH_KEY, t),
  clearRefresh: () => localStorage.removeItem(REFRESH_KEY),
  getVerifSession: () => localStorage.getItem(VERIF_SESSION_KEY),
  setVerifSession: (t) => t && localStorage.setItem(VERIF_SESSION_KEY, t),
  clearVerifSession: () => localStorage.removeItem(VERIF_SESSION_KEY),
  getPendingEmail: () => localStorage.getItem(PENDING_EMAIL_KEY),
  setPendingEmail: (e) => e && localStorage.setItem(PENDING_EMAIL_KEY, e),
  clearPendingEmail: () => localStorage.removeItem(PENDING_EMAIL_KEY),
  clearAll: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(VERIF_SESSION_KEY);
    localStorage.removeItem(PENDING_EMAIL_KEY);
  },
};

const api = axios.create({
  baseURL: XANO_URL,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const t = tokenStore.get();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

let refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      const rt = tokenStore.getRefresh();
      if (rt) {
        original._retry = true;
        try {
          refreshing =
            refreshing ||
            axios.post(`${XANO_URL}/api:brontoy-auth-prod/refresh`, {
              refresh_token: rt,
              refreshToken: rt,
            });
          const res = await refreshing;
          refreshing = null;
          const payload = unwrap(res.data);
          const newToken = payload?.auth_token || payload?.authToken || payload?.token;
          const newRefresh = payload?.refresh_token || payload?.refreshToken;
          if (newToken) {
            tokenStore.set(newToken);
            if (newRefresh) tokenStore.setRefresh(newRefresh);
            original.headers.Authorization = `Bearer ${newToken}`;
            return api(original);
          }
        } catch {
          refreshing = null;
          tokenStore.clearAll();
          // Only force-bounce to /login for auth-required app-shell
          // paths. Guests hitting a public `/:username` profile
          // (whose stale token just failed to refresh) should stay on
          // the profile and let the 10s Guest Sign-in sheet decide
          // when to convert them.
          if (typeof window !== "undefined") {
            const p = window.location.pathname;
            const authedShellPrefixes = [
              "/home", "/discover", "/opinions", "/notifications", "/search",
              "/create", "/reels", "/settings", "/profile", "/saved", "/post/",
              "/onboarding", "/following",
            ];
            const isAuthedShellPath = authedShellPrefixes.some(
              (pp) => p === pp || p.startsWith(pp + "/")
            );
            if (isAuthedShellPath && !p.startsWith("/login")) {
              window.location.href = "/login";
            }
          }
        }
      } else if (typeof window !== "undefined") {
        const p = window.location.pathname;
        const publicPaths = ["/", "/login", "/signup", "/verify", "/forgot", "/reset", "/terms", "/privacy", "/guidelines"];
        // App-shell paths that REQUIRE auth — any 401 here must bounce
        // to /login. Everything else (including `/:username` public
        // profile pages, which a guest is legitimately allowed to view)
        // stays put and lets the page render whatever it can.
        const authedShellPrefixes = [
          "/home", "/discover", "/opinions", "/notifications", "/search",
          "/create", "/reels", "/settings", "/profile", "/saved", "/post/",
          "/onboarding", "/following",
        ];
        const isPublicPath = publicPaths.some((pp) => p === pp || p.startsWith(pp + "/"));
        const isAuthedShellPath = authedShellPrefixes.some(
          (pp) => p === pp || p.startsWith(pp + "/") || p.startsWith(pp)
        );
        if (!isPublicPath && isAuthedShellPath) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(err);
  }
);

// Xano wraps responses as { success, data }. Sometimes data is a JSON string.
// Xano ALSO returns exception envelopes as HTTP 200 with body
// { payload: "...", statement: "Throw Error" } — treat those as errors.
export function unwrap(res) {
  if (res == null) return null;
  if (typeof res === "object" && res.statement === "Throw Error") {
    // Xano exception envelope masquerading as HTTP 200
    const msg = typeof res.payload === "string" ? res.payload : (res.payload?.message || "Request failed.");
    const err = new Error(msg);
    err.xanoEnvelope = res;
    throw err;
  }
  if (typeof res === "object" && "data" in res) {
    let d = res.data;
    if (typeof d === "string") {
      try {
        d = JSON.parse(d);
      } catch {
        /* keep as string */
      }
    }
    // Nested Xano throw?
    if (d && typeof d === "object" && d.statement === "Throw Error") {
      const msg = typeof d.payload === "string" ? d.payload : (d.payload?.message || "Request failed.");
      const err = new Error(msg);
      err.xanoEnvelope = d;
      throw err;
    }
    return d;
  }
  return res;
}

export function apiError(err) {
  const r = err?.response?.data;
  if (r?.message) return r.message;
  if (typeof r === "string") return r;
  return err?.message || "Something went wrong. Please try again.";
}

export default api;
